import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Ban, Download, Euro, PackageMinus, RotateCcw, Search, Settings2, Snail, Warehouse, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { toast } from "sonner";
import { usePermission, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton, Tip } from "@/components/ui";
import { api, ApiError, canManage, isDemo, type SlowMoverClass, type SlowMoverItem, type SlowMoverQuery } from "@/lib/api";
import { fmtNumber } from "@/lib/format";
import { DialogShell, NumField } from "./Reach";
import { intlLocale, t, msg } from "@/lib/i18n";

const PAGE_SIZE = 100;
const eur = (n: number | null | undefined, digits = 0) => (n == null ? "—" : n.toLocaleString(intlLocale(), { style: "currency", currency: "EUR", maximumFractionDigits: digits, minimumFractionDigits: digits }));
const CLS: Record<SlowMoverClass, { label: string; tone: "danger" | "warning" | "neutral" | "success" }> = {
  dead: { label: msg("Ohne Absatz"), tone: "danger" },
  slow: { label: msg("Langsamdreher"), tone: "warning" },
  new: { label: msg("Zu früh"), tone: "neutral" },
  ok: { label: msg("Läuft"), tone: "success" },
};
type Filter = NonNullable<SlowMoverQuery["filter"]>;
const CHIPS: Filter[] = ["action", "dead", "slow", "new", "all"];
const chipLabel = (f: Filter) => (f === "action" ? "Handlungsbedarf" : f === "all" ? "Alle mit Bestand" : CLS[f].label);

/** Ladenhüter-Analyse (Reiter unter „Bestände“): was liegt, was kostet es, was tun */
export function SlowMoversPanel() {
  const { tenantId, role } = useTenant();
  const mayEdit = usePermission()("stock", "edit");
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const filter = (params.get("filter") as Filter | null) ?? "action";
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setTerm(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);
  const [sort, setSort] = useState<NonNullable<SlowMoverQuery["sort"]>>("capital");
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [term, filter, sort]);
  const query: SlowMoverQuery = { filter, sort, page, pageSize: PAGE_SIZE, ...(term ? { search: term } : {}) };
  const q = useQuery({ queryKey: ["slow-movers", tenantId, query], queryFn: () => api.stock.slowMovers(tenantId, query), placeholderData: keepPreviousData });
  const setFilter = (f: Filter) => setParams((p) => { const n = new URLSearchParams(p); n.set("view", "slow"); if (f === "action") n.delete("filter"); else n.set("filter", f); return n; }, { replace: true });
  const [settingsOpen, setSettingsOpen] = useState(false);

  const discontinue = useMutation({
    mutationFn: (p: { item: SlowMoverItem; on: boolean }) => api.stock.setDiscontinued(tenantId, p.item.productId, p.on),
    onSuccess: (_r, p) => {
      toast.success(p.on ? `${p.item.sku} wird nicht mehr nachbestellt` : `${p.item.sku} wird wieder nachbestellt`, { description: p.on ? "Erscheint nicht mehr in Bestellvorschlägen und Warnungen." : undefined });
      void qc.invalidateQueries({ queryKey: ["slow-movers", tenantId] });
      void qc.invalidateQueries({ queryKey: ["reach", tenantId] });
      void qc.invalidateQueries({ queryKey: ["action-items", tenantId] });
    },
    onError: () => toast.error("Nicht gespeichert"),
  });

  const d = q.data;
  const s = d?.summary;
  const items = d?.items ?? [];
  const pages = Math.max(1, Math.ceil((d?.total ?? 0) / PAGE_SIZE));
  const count = (f: Filter) => (!d ? null : f === "action" ? d.counts.dead + d.counts.slow : f === "all" ? d.counts.dead + d.counts.slow + d.counts.new + d.counts.ok : d.counts[f]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={<Archive className="size-4" />} label={t("Ohne Absatz")} value={d ? fmtNumber(d.counts.dead) : null} sub={d ? t("seit {deadDays}+ Tagen kein Verkauf", { deadDays: d.settings.deadDays }) : ""} tone={d?.counts.dead ? "danger" : undefined} onClick={() => setFilter("dead")} />
        <Kpi icon={<Snail className="size-4" />} label={t("Langsamdreher")} value={d ? fmtNumber(d.counts.slow) : null} sub={d ? t("Bestand reicht > {overstockDays} Tage", { overstockDays: d.settings.overstockDays }) : ""} tone={d?.counts.slow ? "warning" : undefined} onClick={() => setFilter("slow")} />
        <Kpi
          icon={<Euro className="size-4" />}
          label={t("Kapital im Überschuss")}
          value={s ? eur(s.excessCapital) : null}
          sub={s ? (s.withoutPrice ? t("{v} Stück · für {withoutPrice} Artikel fehlt der Einkaufspreis", { v: fmtNumber(s.excessUnits), withoutPrice: s.withoutPrice }) : t("{v} Stück{v2}", { v: fmtNumber(s.excessUnits), v2: s.stockCapital ? t(" · {v} % des Bestandswerts", { v: Math.round(((s.excessCapital ?? 0) / s.stockCapital) * 100) }) : "" })) : ""}
          tone={s?.withoutPrice ? "warning" : undefined}
        />
        <Kpi
          icon={<Warehouse className="size-4" />}
          label={t("Lagerkosten je Monat")}
          value={!d ? null : s?.storageMonthly != null ? eur(s.storageMonthly) : "—"}
          sub={!d ? "" : d.settings.storageCostPerM3Day == null ? (canManage(role) ? t("Satz je m³ und Tag hinterlegen →") : t("kein Satz hinterlegt")) : `${t("Volumen × {rate}/m³ je Tag × 30", { rate: eur(d.settings.storageCostPerM3Day, 2) })}${s?.storageSinceInbound != null ? ` · ${t("seit Wareneingang {sum}", { sum: eur(s.storageSinceInbound) })}` : ""}${s?.withoutVolume ? ` · ${t("{n} ohne Maße", { n: s.withoutVolume })}` : ""}`}
          onClick={d?.settings.storageCostPerM3Day == null && canManage(role) ? () => setSettingsOpen(true) : undefined}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {CHIPS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={cx("inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[12.5px] font-medium transition-colors", filter === f ? "bg-ink text-ink-fg border-ink" : "bg-surface border-border hover:bg-surface-2")}>
            {chipLabel(f)} <span className="tabular-nums opacity-70">{count(f) ?? "·"}</span>
          </button>
        ))}
      </div>

      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Suchen: SKU, Artikel oder Lieferant …")} aria-label={t("Ladenhüter durchsuchen")} leading={<Search className="size-4" />} suffix={search ? <button onClick={() => setSearch("")} className="size-6 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Suche leeren")}><X className="size-3.5" /></button> : undefined} className="h-11 text-[14px]" />

      <div className="flex flex-wrap items-center gap-2">
        <select aria-label={t("Sortierung")} value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} className="h-8 rounded-lg border border-border bg-surface px-2.5 text-[12.5px]">
          <option value="capital">{t("Teuerster Überschuss zuerst")}</option>
          <option value="units">{t("Meiste Stück zuerst")}</option>
          <option value="lastSale">{t("Am längsten unverkauft")}</option>
          <option value="sku">{t("SKU")}</option>
        </select>
        {d && (
          <button onClick={() => canManage(role) && setSettingsOpen(true)} className={cx("inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-border text-[12.5px] text-fg-muted", canManage(role) && "hover:bg-surface-2 hover:text-fg")} title={canManage(role) ? t("Schwellen und Lagerkosten ändern") : t("Nur Administratoren können das ändern")}>
            <Settings2 className="size-3.5" /> {t("ohne Absatz ab")} {d.settings.deadDays} {t("T. · Überbestand ab")} {d.settings.overstockDays} T.
          </button>
        )}
        <a href={isDemo ? undefined : api.stock.slowMoversCsvUrl(tenantId, query)} onClick={isDemo ? () => toast.info("Im Demo-Modus nicht verfügbar") : undefined} className="ml-auto">
          <Button size="sm" variant="ghost" icon={<Download className="size-3.5" />}>{t("Liste (CSV)")}</Button>
        </a>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="text-[12px] text-fg-subtle border-b border-border bg-surface-2/50">
              <tr>
                <th className="h-9 pl-4 pr-3 font-medium">{t("Artikel · Empfehlung")}</th>
                <th className="h-9 px-3 font-medium text-right"><Tip content={t("Physischer Bestand im Lager")}><span>{t("Bestand")}</span></Tip></th>
                <th className="h-9 px-3 font-medium">{t("Letzter Verkauf")}</th>
                <th className="h-9 px-3 font-medium text-right"><Tip content={t("Verkaufte Stück in den letzten 90 Tagen – darunter: 365 Tage (inkl. Shop-Historie)")}><span>{t("Verkauft")}</span></Tip></th>
                <th className="h-9 px-3 font-medium text-right"><Tip content={t("Ohne Absatz: der ganze Bestand. Langsamdreher: alles über der Überbestand-Schwelle.")}><span>{t("Überschuss")}</span></Tip></th>
                <th className="h-9 px-3 font-medium text-right"><Tip content={t("Überschuss × Einkaufspreis – darunter: ganzer Bestand")}><span>{t("Kapital")}</span></Tip></th>
                <th className="h-9 px-3 font-medium text-right"><Tip content={t("Geschätzt: Volumen des Bestands × Lagerkosten je m³ und Tag × 30 – darunter: seit dem letzten Wareneingang (Satz × Volumen × Liegetage)")}><span>{t("Lager/Monat")}</span></Tip></th>
                <th className="h-9 pl-3 pr-4 font-medium" />
              </tr>
            </thead>
            <tbody>
              {q.isLoading && Array.from({ length: 6 }, (_, i) => <tr key={i} className="border-b border-border"><td colSpan={8} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td></tr>)}
              {items.map((r) => (
                <tr key={r.productId} className="border-b border-border last:border-0 hover:bg-surface-2/50 align-top">
                  <td className="pl-4 pr-3 py-2.5 min-w-[260px] max-w-[420px]">
                    <div className="flex items-center gap-2">
                      <Link to={`/products?open=${r.productId}`} className="font-medium hover:underline truncate">{r.name}</Link>
                      {r.discontinued && <Badge>{t("Auslaufartikel")}</Badge>}
                    </div>
                    <div className="text-[11.5px] text-fg-muted"><span className="font-mono">{r.sku}</span>{r.supplier && <> · {r.supplier}</>}</div>
                    {r.advice && <p className="mt-1 text-[12px] text-fg-muted leading-snug">{r.advice}</p>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtNumber(r.onHand)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-[13px]">
                    {r.lastSaleDays == null ? <span className="text-fg-subtle">{t("nie")}</span> : r.lastSaleDays === 0 ? t("heute") : <>{t("vor")} {fmtNumber(r.lastSaleDays)} T.</>}
                    {r.reachDays != null && <div className="text-[11px] text-fg-subtle">{t("reicht")} {r.reachDays >= 730 ? t("> 2 Jahre") : `${fmtNumber(r.reachDays)} T.`}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                    <div className="text-[13px]">{fmtNumber(r.sold90)} <span className="text-fg-subtle text-[11px]">{t("in 90 T.")}</span></div>
                    <div className="text-[11px] text-fg-subtle">{fmtNumber(r.sold365)} {t("in 365 T.")}</div>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium">{r.excess ? fmtNumber(r.excess) : <span className="text-fg-subtle font-normal">—</span>}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                    {r.purchasePrice == null ? (
                      <Tip content={t("Einkaufspreis in der Reichweite am Artikel hinterlegen (Regler-Symbol)")}><span className="text-[12px] text-warning">{t("Preis fehlt")}</span></Tip>
                    ) : (
                      <>
                        <div className="text-[13px] font-medium">{eur(r.excessCapital)}</div>
                        <div className="text-[11px] text-fg-subtle">{t("von")} {eur(r.capital)}</div>
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[13px]">
                    {r.storageMonthly == null ? <span className="text-fg-subtle">—</span> : eur(r.storageMonthly, 2)}
                    {r.storageSinceInbound != null && r.storageDays != null && <div className="text-[11.5px] text-fg-muted whitespace-nowrap">{t("seit WE {sum} ({days} T.)", { sum: eur(r.storageSinceInbound, 2), days: fmtNumber(r.storageDays) })}</div>}
                  </td>
                  <td className="pl-3 pr-4 py-2.5">
                    <div className="flex items-center gap-2 justify-end">
                      <Badge tone={CLS[r.cls].tone}>{t(CLS[r.cls].label)}</Badge>
                      {mayEdit && (r.cls === "dead" || r.cls === "slow" || r.discontinued) && (
                        <button
                          onClick={() => discontinue.mutate({ item: r, on: !r.discontinued })}
                          disabled={discontinue.isPending}
                          className="size-7 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg"
                          aria-label={r.discontinued ? t("{sku} wieder nachbestellen", { sku: r.sku }) : t("{sku} nicht mehr nachbestellen", { sku: r.sku })}
                          title={r.discontinued ? t("Wieder nachbestellen") : t("Nicht mehr nachbestellen (Auslaufartikel)")}
                        >
                          {r.discontinued ? <RotateCcw className="size-3.5" /> : <Ban className="size-3.5" />}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!q.isLoading && items.length === 0 && (
          <EmptyState icon={<PackageMinus className="size-5" />} title={filter === "action" ? t("Keine Ladenhüter") : t("Keine Artikel für diesen Filter")}>
            {filter === "action" ? t("Alle Artikel mit Bestand verkaufen sich – nichts liegt zu lange.") : t("Passe Filter oder Suche an.")}
          </EmptyState>
        )}
        {(d?.total ?? 0) > PAGE_SIZE && (
          <div className="flex items-center gap-3 px-4 py-2.5 border-t border-border text-[12.5px] text-fg-muted">
            <span className="tabular-nums">{fmtNumber((page - 1) * PAGE_SIZE + 1)}–{fmtNumber(Math.min(d!.total, page * PAGE_SIZE))} {t("von")} {fmtNumber(d!.total)}</span>
            <div className="ml-auto flex items-center gap-1">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t("‹ Zurück")}</Button>
              <span className="tabular-nums px-1">{page} / {fmtNumber(pages)}</span>
              <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>{t("Weiter ›")}</Button>
            </div>
          </div>
        )}
      </Card>
      <p className="text-[12px] text-fg-subtle">
        {t("Absatz aus euren Aufträgen und der übernommenen Shop-Historie (bis 2 Jahre). Frisch angelieferte Artikel ohne Verkauf werden erst nach")} {d?.settings.deadDays ?? 180} {t("Tagen bewertet. Einkaufspreise und „nicht mehr nachbestellen“ pflegt ihr auch in der Reichweite am Artikel.")}
      </p>
      {d && <SlowMoverSettings open={settingsOpen} onOpenChange={setSettingsOpen} />}
    </div>
  );
}

function Kpi({ icon, label, value, sub, tone, onClick }: { icon: React.ReactNode; label: string; value: string | null; sub: string; tone?: "danger" | "warning"; onClick?: (() => void) | undefined }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp onClick={onClick} className={cx("text-left rounded-xl border border-border bg-surface p-4 transition-shadow", onClick && "hover:shadow-md hover:border-border-strong")}>
      <div className="flex items-center gap-2 text-fg-muted text-[12.5px]"><span className="text-fg-subtle">{icon}</span>{t(label)}</div>
      <div className="mt-2 text-[24px] font-semibold tracking-tight tabular-nums">{value ?? <Skeleton className="h-8 w-16" />}</div>
      <div className={cx("text-[12px]", tone === "danger" ? "text-danger" : tone === "warning" ? "text-warning" : "text-fg-subtle")}>{t(sub)}</div>
    </Comp>
  );
}

/** Schwellen und Lagerkostensatz – Teil der Nachbestell-Einstellungen (Überbestand gilt auch für die Reichweite) */
function SlowMoverSettings({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const cur = useQuery({ queryKey: ["replenishment-settings", tenantId], queryFn: () => api.stock.replenishmentSettings(tenantId), enabled: open });
  const [v, setV] = useState({ deadDays: "", overstockDays: "", rate: "" });
  useEffect(() => {
    if (open && cur.data) setV({ deadDays: String(cur.data.deadDays), overstockDays: String(cur.data.overstockDays), rate: cur.data.storageCostPerM3Day == null ? "" : String(cur.data.storageCostPerM3Day).replace(".", ",") });
  }, [open, cur.data]);
  const save = useMutation({
    mutationFn: () => api.stock.saveReplenishmentSettings(tenantId, {
      ...cur.data!,
      deadDays: Math.min(730, Math.max(30, Number(v.deadDays) || 180)),
      overstockDays: Math.min(1000, Math.max(30, Number(v.overstockDays) || 180)),
      storageCostPerM3Day: v.rate.trim() ? Math.max(0, Number(v.rate.replace(",", ".")) || 0) : null,
    }),
    onSuccess: () => {
      toast.success("Gespeichert – Analyse ist neu berechnet");
      onOpenChange(false);
      for (const k of ["slow-movers", "reach", "replenishment-settings"]) void qc.invalidateQueries({ queryKey: [k, tenantId] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Ungültige Werte") : "Speichern fehlgeschlagen"),
  });
  return (
    <DialogShell open={open} onOpenChange={onOpenChange} title={t("Ladenhüter – Einstellungen")} description={t("Ab wann ein Artikel als Ladenhüter gilt und was ein Kubikmeter Lagerplatz am Tag kostet.")}>
      {!cur.data ? <Skeleton className="mt-4 h-32" /> : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <NumField label={t("Ohne Absatz ab")} value={v.deadDays} onChange={(x) => setV({ ...v, deadDays: x })} suffix={t("Tagen")} hint={t("ohne Verkauf (30–730)")} />
            <NumField label={t("Überbestand ab")} value={v.overstockDays} onChange={(x) => setV({ ...v, overstockDays: x })} suffix={t("Tagen")} hint={t("Reichweite – gilt auch für die Reichweite")} />
            <label className="block col-span-2">
              <span className="text-[12.5px] font-medium">{t("Lagerkosten je m³ und Tag")}</span>
              <Input className="mt-1" value={v.rate} onChange={(e) => setV({ ...v, rate: e.target.value.replace(/[^\d,.]/g, "") })} inputMode="decimal" placeholder={t("z. B. 0,62")} suffix={t("€ netto")} />
              <span className="mt-1 block text-[11.5px] text-fg-subtle">{t("Satz je m³ und Tag aus eurem Logiful-Angebot. Leer lassen, wenn ihr keine Schätzung wollt. Gerechnet wird mit den Maßen am Artikel: je Monat × 30 Tage, seit Wareneingang × Liegetage.")}</span>
            </label>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button onClick={() => onOpenChange(false)}>{t("Abbrechen")}</Button>
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button>
          </div>
        </>
      )}
    </DialogShell>
  );
}
